import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Project, DashboardMetric } from '../../types';
import { dossierAPI } from '../../services/api';
import { BRAND, faseKleur } from '../../styles/brand';

interface DashboardModuleProps {
  project: Project;
  metrics?: DashboardMetric[];
}

interface Fase {
  key: string;
  module: string;
  nr: number;
  label: string;
  rol: string;
  aiLed: boolean;
}

const FASEN: Fase[] = [
  { key: 'projectverkenning', module: 'projectverkenning', nr: 1, label: 'Projectverkenning', rol: 'Mens leidt · AI faciliteert', aiLed: false },
  { key: 'omgevingsanalyse', module: 'omgevingsanalyse', nr: 2, label: 'Omgevingsanalyse', rol: 'AI ondersteunt · mens verrijkt', aiLed: true },
  { key: 'personas', module: 'personas-fase', nr: 3, label: "Persona's", rol: 'AI genereert · mens beoordeelt', aiLed: true },
  // \u00AD = zacht afbreekstreepje: de lange namen breken netjes af als de kolom te smal is.
  { key: 'strategie', module: 'strategie-fase', nr: 4, label: 'Communicatie\u00ADstrategie', rol: 'AI stelt voor · mens beslist', aiLed: true },
  { key: 'kernboodschap', module: 'kernboodschap', nr: 5, label: 'Kernboodschap', rol: 'Mens schrijft · AI toetst', aiLed: false },
  { key: 'communicatiewerkplaats', module: 'werkplaats', nr: 6, label: 'Communicatie\u00ADwerkplaats', rol: 'AI schrijft · mens redigeert', aiLed: true },
];
/** Minimale breedte van één stap in de tijdlijn; smaller dan dat schuift de tijdlijn horizontaal. */
const STAP_MIN = 130;

const statusLabels: Record<string, string> = { planning: 'In afwachting', active: 'Actief', completed: 'Voltooid', paused: 'Gepauzeerd', cancelled: 'Geannuleerd' };

function useIsMobile(bp = 768) {
  const [m, setM] = useState(typeof window !== 'undefined' && window.innerWidth <= bp);
  useEffect(() => {
    const on = () => setM(window.innerWidth <= bp);
    on();
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, [bp]);
  return m;
}

export const DashboardModule: React.FC<DashboardModuleProps> = ({ project }) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [sections, setSections] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dossierAPI.getByProject(project.id);
      setSections(res?.dossier?.sections || {});
    } catch (e) {
      setSections({});
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  useEffect(() => { load(); }, [load]);

  const go = (module: string) => navigate(`/project/${project.id}/${module}`);

  const faseStates = FASEN.map((f) => {
    const s = sections[f.key] || {};
    const locked = s.status === 'locked';
    const hasContent = s.content && Object.values(s.content).some((v) => (Array.isArray(v) ? v.length > 0 : (v || '').toString().trim().length > 0));
    return { ...f, locked, hasContent };
  });

  const gevalideerd = faseStates.filter((f) => f.locked).length;
  const activeIdx = faseStates.findIndex((f) => !f.locked); // -1 als alles af
  const active = activeIdx >= 0 ? faseStates[activeIdx] : null;
  const pct = Math.round((gevalideerd / FASEN.length) * 100);

  const headerImage = (project as any).headerImage as string | undefined;
  const period = project.period || (project.startDate && project.endDate
    ? `${new Date(project.startDate).toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' })} - ${new Date(project.endDate).toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' })}`
    : project.startDate ? new Date(project.startDate).toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' }) : '');

  const heroBg = headerImage
    ? `linear-gradient(90deg, rgba(20,40,32,0.94) 0%, rgba(20,40,32,0.78) 34%, rgba(20,40,32,0.25) 62%, rgba(20,40,32,0) 82%), url(${headerImage})`
    : `linear-gradient(135deg, ${BRAND.groen} 0%, ${BRAND.groenHover} 100%)`;

  return (
    <div>
      {/* HERO — full-bleed: vult het volledige contentgebied (geen max-breedte/marge) */}
      <div style={{ position: 'relative', overflow: 'hidden', minHeight: isMobile ? 340 : 540, background: heroBg, backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div style={{ position: 'relative', maxWidth: 1180, margin: '0 auto', padding: isMobile ? '28px 16px 104px' : '56px 32px 152px' }}>
          <div style={{ maxWidth: 620 }}>
          <span style={{ display: 'inline-block', fontSize: 12.5, fontWeight: 700, color: BRAND.groen, background: active ? faseKleur(active.nr).base : BRAND.tint, borderRadius: 8, padding: '5px 12px', marginBottom: isMobile ? 14 : 18 }}>
            {active ? active.label : 'Voltooid'}
          </span>
          <h1 style={{ margin: '0 0 12px', fontSize: isMobile ? 25 : 34, lineHeight: 1.12, color: '#fff', fontWeight: 700, letterSpacing: '-0.5px' }}>{project.name}</h1>
          {((project as any).shortDescription || project.description) && (
            <p style={{ margin: '0 0 20px', fontSize: isMobile ? 14.5 : 16, color: 'rgba(255,255,255,0.9)', lineHeight: 1.5, maxWidth: 520 }}>
              {(project as any).shortDescription || project.description}
            </p>
          )}
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', color: 'rgba(255,255,255,0.85)', fontSize: 14 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>
              {project.location}
            </span>
            {period && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                {period}
              </span>
            )}
          </div>
          </div>
        </div>
      </div>

      {/* Inhoud — kaart op de huidige breedte, overlapt de bredere hero */}
      <div style={{ maxWidth: 1180, margin: '0 auto', padding: isMobile ? '0 16px 16px' : '0 32px 24px' }}>
      {/* PROJECTVOORTGANG — overlapt de hero */}
      <div style={{ position: 'relative', zIndex: 2, marginTop: isMobile ? -60 : -76, background: '#fff', border: '1px solid #eef0f4', borderRadius: 16, padding: isMobile ? '20px 16px 18px' : '28px 28px 24px', boxShadow: '0 8px 30px rgba(0,0,0,0.12)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 8 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: '#1E3A2F', letterSpacing: '-0.3px' }}>Projectvoortgang</h2>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <button onClick={() => go('dossier')} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 16px', borderRadius: 10, fontSize: 14, fontWeight: 600, border: '1px solid #d8dce4', background: '#fff', color: '#374151', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="8" y1="13" x2="16" y2="13" /><line x1="8" y1="17" x2="16" y2="17" /></svg>
              Volledig dossier
            </button>
            {active && (
              <button onClick={() => go(active.module)} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 18px', borderRadius: 10, fontSize: 14, fontWeight: 600, border: 'none', background: '#1e3a2f', color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                Ga verder
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
              </button>
            )}
          </div>
        </div>

        {/* STEPPER — raster met drie rijen (cirkel, naam, status), zodat de statussen op één lijn blijven als een lange naam afbreekt */}
        <div style={{ overflowX: 'auto', overflowY: 'visible', paddingTop: active ? 78 : 28 }}>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${faseStates.length}, minmax(${STAP_MIN}px, 1fr))`, minWidth: STAP_MIN * faseStates.length }}>
            {faseStates.map((f, i) => {
              const isActive = i === activeIdx;
              const fk = faseKleur(f.nr);
              // connector naar de volgende stap
              const connectorBg = f.locked ? BRAND.groen : '#e6e8ee';
              const kolom = i + 1;
              return (
                <React.Fragment key={f.key}>
                  <div style={{ gridColumn: kolom, gridRow: 1, position: 'relative', textAlign: 'center' }}>
                    {/* connector */}
                    {i < faseStates.length - 1 && (
                      <div style={{ position: 'absolute', top: 19, left: '50%', width: '100%', height: 3, background: connectorBg, zIndex: 0 }} />
                    )}
                    {/* callout boven actieve fase */}
                    {isActive && (
                      <div style={{ position: 'absolute', bottom: 'calc(100% - 2px)', left: '50%', transform: 'translateX(-50%)', zIndex: 3, whiteSpace: 'nowrap' }}>
                        <div style={{ background: '#fff', border: `1.5px solid ${fk.border}`, borderRadius: 10, padding: '7px 12px', boxShadow: '0 4px 12px rgba(30,58,47,0.12)', fontSize: 12, lineHeight: 1.3, color: BRAND.groen }}>
                          <div style={{ fontWeight: 700 }}>Huidige fase</div>
                          <div>{f.label}</div>
                        </div>
                      </div>
                    )}
                    {/* circle */}
                    <div
                      onClick={() => go(f.module)}
                      style={{
                        position: 'relative', zIndex: 2, width: 40, height: 40, borderRadius: '50%', margin: '0 auto',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                        fontSize: 15, fontWeight: 700,
                        // Kleur per fase conform het compas; gevalideerd = donkergroen lock-symbool.
                        background: f.locked ? BRAND.groen : (isActive ? fk.base : fk.soft),
                        color: f.locked ? '#fff' : BRAND.groen,
                        border: !f.locked && !isActive ? `2px solid ${fk.border}` : 'none',
                        boxSizing: 'border-box',
                        boxShadow: f.locked ? `0 0 0 4px ${fk.base}` : (isActive ? `0 0 0 6px ${fk.border}` : 'none'),
                      }}
                    >
                      {f.locked ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
                      ) : f.nr}
                    </div>
                  </div>
                  {/* label + status */}
                  <div onClick={() => go(f.module)} style={{ gridColumn: kolom, gridRow: 2, marginTop: 12, padding: '0 6px', textAlign: 'center', fontSize: 14, fontWeight: 600, lineHeight: 1.3, color: BRAND.groen, cursor: 'pointer' }}>{f.label}</div>
                  <div style={{ gridColumn: kolom, gridRow: 3, marginTop: 6, paddingBottom: 4, textAlign: 'center' }}>
                    <span style={{ fontSize: 11.5, fontWeight: 600, borderRadius: 6, padding: '3px 9px', background: f.locked ? BRAND.groen : (f.hasContent ? fk.soft : '#f1f2f5'), color: f.locked ? '#fff' : (f.hasContent ? BRAND.groen : '#8a90a0') }}>
                      {f.locked ? 'Gevalideerd' : (f.hasContent ? 'In bewerking' : 'Nog niet gestart')}
                    </span>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {loading && <div style={{ marginTop: 12, fontSize: 12.5, color: '#b6bcc7', textAlign: 'center' }}>Projectdossier laden…</div>}
      </div>
    </div>
  );
};
